// @ts-strict-ignore
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { toSelectOptions } from "components/layout/Sidebar/SidebarControls/ClusterFilter/cluster-filter.utils";
import { SidebarControlContentActions } from "components/layout/Sidebar/SidebarControls/SidebarControl/SidebarControlContent/SidebarControlContentActions";
import { SidebarControlContentTitle } from "components/layout/Sidebar/SidebarControls/SidebarControl/SidebarControlContent/SidebarControlContentTitle";
import { SidebarControlToggleFunctionProp } from "components/layout/Sidebar/sidebar.types";
import { DataContent } from "components/ui/DataContent";
import { TreeMultiSelect, Loading } from "components/ui-deprecated";
import { Button } from "components/ui-deprecated/Button";
import { useSendUserEvent } from "hooks/user-event/useSendUserEvent";
import { useAppDispatch, useAppSelector } from "store";
import { apiJS } from "store/api";
import { useInvalidateDataSourceCache } from "store/hooks/useInvalidateDataSourceCache";
import { useGetUserClustersQuery, useSetUserClustersMutation } from "store/services/cluster-filter.service";
import { TAG_USER, userApi } from "store/services/user.service";
import { setClusters as setClustersLocally } from "store/slices/user.slice";
import { GUID } from "types/common.types";
import { UserCluster } from "types/user.types";

type FormValues = {
    clusters: GUID[];
};

const validationSchema = Yup.object({
    clusters: Yup.array(Yup.string()),
});

type Props = SidebarControlToggleFunctionProp & {
    selectedClusters: UserCluster[];
};

const ClusterFilterContent = (props: Props) => {
    const { selectedClusters, handleToggleOpen } = props;

    const userId = useAppSelector((state) => state.user.id);

    const clustersDataSource = useGetUserClustersQuery({ userId });
    const { data: clusters } = clustersDataSource;

    const clusterSelectOptions = toSelectOptions({ clusters });
    const initialSelectedClusters = selectedClusters.map((m) => m.id);

    const [setClusters] = useSetUserClustersMutation();

    const dispatch = useAppDispatch();
    const invalidateDataSourceCache = useInvalidateDataSourceCache();
    const sendUserEvent = useSendUserEvent();

    const handleSubmit = async (data: FormValues) => {
        try {
            const clustersWithNames = data.clusters.map((clusterId) => ({
                id: clusterId,
                name: clusters.find((cluster) => cluster.id === clusterId)?.name ?? "Unknown cluster",
            }));

            await setClusters({ userId, clusterIds: data.clusters }).unwrap();
            invalidateDataSourceCache();
            dispatch(userApi.util.invalidateTags([TAG_USER]));
            dispatch(setClustersLocally(clustersWithNames));
            dispatch(apiJS.util.resetApiState());
            handleToggleOpen();

            sendUserEvent({
                type: "select:clusters",
                value: clustersWithNames.map((cluster) => cluster.name).join(", "),
            });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    };

    return (
        <DataContent dependencies={[clustersDataSource]}>
            <Formik<FormValues>
                initialValues={{
                    clusters: initialSelectedClusters,
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
            >
                {({ dirty, isSubmitting }) => (
                    <Form>
                        <SidebarControlContentTitle>
                            Lely Center Filter
                            <SidebarControlContentActions>
                                <Button variant={"text"} type={"reset"} disabled={!dirty || isSubmitting}>
                                    Reset
                                </Button>
                                <Loading show={isSubmitting} style={{ margin: "auto 0" }}>
                                    <Button type={"submit"} disabled={!dirty || isSubmitting}>
                                        Apply
                                    </Button>
                                </Loading>
                            </SidebarControlContentActions>
                        </SidebarControlContentTitle>
                        <TreeMultiSelect name={"clusters"} options={clusterSelectOptions} />
                    </Form>
                )}
            </Formik>
        </DataContent>
    );
};

export { ClusterFilterContent };
